import {Fragment} from 'react';
import {
    Box,
    Button,
    Dialog,
    Flex,
    Heading,
    HStack,
    Image,
    Kbd,
    Portal,
    Separator,
    Table,
    Text,
    VStack,
} from '@chakra-ui/react';
import {DialogCloseButton} from './ui/dialog-close-button';
import {version} from '../env';

interface Shortcut {
    /** Alternative combinations; the keys of one combination are pressed together. */
    combos: string[][];
    description: string;
    /** Where the shortcut works, if not everywhere in its view. */
    note?: string;
}

interface ShortcutGroup {
    title: string;
    shortcuts: Shortcut[];
}

const WEB_BIN_ONLY = 'csak a web mappában';

// Keep in sync with the key handlers of FolderBrowserPage, the viewer (useViewerShortcuts and the
// actions' keys in useViewerActions) and CommentComposer.
const SHORTCUT_GROUPS: ShortcutGroup[] = [
    {
        title: 'Rácsnézet',
        shortcuts: [
            //{combos: [['Kattintás']], description: 'Elem kijelölése'},
            //{combos: [['← ↑ → ↓']], description: 'Kijelölés mozgatása'},
            //{combos: [['Shift', '← ↑ → ↓'], ['Shift', 'Kattintás']], description: 'Tartomány kijelölése'},
            //{combos: [['Ctrl/⌘', 'Kattintás']], description: 'Elem hozzáadása a kijelöléshez vagy eltávolítása belőle'},
            {combos: [['Ctrl/⌘', 'A']], description: 'Az összes kijelölése'},
            {combos: [['F']], description: 'Keresés a mappában'},
            {combos: [['E'], ['Space']], description: 'Kijelölt kép megnyitása teljes képernyőn', note: 'több kijelölt közül az utoljára kattintott'},
            {combos: [['N']], description: 'Fájlnév másolása', note: 'ha egy elem van kijelölve'},
            //{combos: [['Dupla kattintás']], description: 'Mappa megnyitása, kép megnyitása teljes képernyőn'},
            //{combos: [['Jobb kattintás']], description: 'Műveletek menüje'},
            {combos: [['S']], description: 'Plazmába másolás / eltávolítás a plazmából', note: WEB_BIN_ONLY},
            {combos: [['X']], description: 'Kijelöltek archívumba helyezése', note: WEB_BIN_ONLY},
            {combos: [['Delete'], ['Backspace']], description: 'Kijelöltek törlése'},
        ],
    },
    {
        title: 'Teljes képernyős nézet',
        shortcuts: [
            {combos: [['←'], ['→']], description: 'Előző / következő kép'},
            {combos: [['Esc'], ['G']], description: 'Bezárás'},
            {combos: [['+'], ['-']], description: 'Nagyítás / kicsinyítés', note: 'görgővel és dupla kattintással is'},
            {combos: [['0']], description: 'Vissza a teljes képhez'},
            {combos: [['I']], description: 'Információk (hisztogram, fájladatok) megjelenítése/elrejtése'},
            {combos: [['C']], description: 'Verziók és megjegyzések megjelenítése/elrejtése'},
            {combos: [['N']], description: 'Fájlnév másolása'},
            {combos: [['S']], description: 'Plazmába másolás / eltávolítás a plazmából', note: WEB_BIN_ONLY},
            {combos: [['X']], description: 'Archívumba helyezés', note: WEB_BIN_ONLY},
            {combos: [['Delete'], ['Backspace']], description: 'Törlés'},
            //{combos: [['Jobb kattintás']], description: 'Műveletek menüje'},
        ],
    },
    {
        title: 'Megjegyzés írása (teljes képernyős nézet)',
        shortcuts: [
            {combos: [['Enter']], description: 'Küldés'},
            {combos: [['Shift', 'Enter']], description: 'Új sor'},
            {combos: [['Esc']], description: 'Válasz megszakítása'},
            {combos: [['Esc']], description: 'Terület kijelölésének megszakítása a képen'},
        ],
    },
];

interface AboutModalProps {
    isOpen: boolean;
    onClose: () => void;
}

/** "Névjegy": logo, version, author, and every keyboard shortcut by view. */
export function AboutModal({isOpen, onClose}: AboutModalProps) {
    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(e) => {
                if (!e.open) onClose();
            }}
            size="xl"
            scrollBehavior="inside"
        >
            <Portal>
            <Dialog.Backdrop/>
            <Dialog.Positioner>
            <Dialog.Content>
                <Dialog.Header><Dialog.Title>Névjegy</Dialog.Title></Dialog.Header>
                <DialogCloseButton/>
                <Dialog.Body>
                    <HStack gap={4} mb={5}>
                        <Image src="/logo192.png" alt="SPOT logó" boxSize="64px" borderRadius="md"/>
                        <VStack align="start" gap={0}>
                            <Heading as="h3" size="md">SPOT Albumkezelő</Heading>
                            <Text color="gray.600">Verzió: {version()}</Text>
                            <Text color="gray.600">Készítette: Telek Benjámin</Text>
                        </VStack>
                    </HStack>
                    <Separator mb={4}/>
                    <Heading as="h3" size="md" mb={3}>Billentyűparancsok</Heading>
                    <VStack align="stretch" gap={5}>
                        {SHORTCUT_GROUPS.map((group) => (
                            <Box key={group.title}>
                                <Heading as="h4" size="sm" mb={2}>{group.title}</Heading>
                                <Table.Root size="sm">
                                    <Table.Body>
                                        {group.shortcuts.map((shortcut) => (
                                            <Table.Row key={shortcut.description}>
                                                <Table.Cell pl={0}>
                                                    {shortcut.description}
                                                    {shortcut.note &&
                                                        <Text as="span" color="gray.500" fontSize="xs"> ({shortcut.note})</Text>
                                                    }
                                                </Table.Cell>
                                                <Table.Cell pr={0}>
                                                    <ShortcutKeys combos={shortcut.combos}/>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table.Root>
                            </Box>
                        ))}
                    </VStack>
                </Dialog.Body>
                <Dialog.Footer>
                    <Button colorPalette="blue" onClick={onClose}>Bezárás</Button>
                </Dialog.Footer>
            </Dialog.Content>
            </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
}

function ShortcutKeys({combos}: {combos: string[][]}) {
    return (
        <Flex wrap="wrap" gap={1} justify="flex-end" align="center">
            {combos.map((combo, i) => (
                <Fragment key={combo.join('+')}>
                    {i > 0 && <Text as="span" color="gray.400">/</Text>}
                    <HStack gap={1}>
                        {combo.map((key, j) => (
                            <Fragment key={key}>
                                {j > 0 && <Text as="span" color="gray.400">+</Text>}
                                <Kbd whiteSpace="nowrap">{key}</Kbd>
                            </Fragment>
                        ))}
                    </HStack>
                </Fragment>
            ))}
        </Flex>
    );
}
